import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, Copy, ShieldCheck, Smartphone } from 'lucide-react';
import { Alert, Button, Card, CardContent, Field, Input } from '@/components/ui';
import { AuthShell } from '@/layouts/AuthShell';
import { api, tokens } from '@/lib/api';
import { useAuth } from '@/lib/auth';

interface SetupResponse {
  secret: string;
  otpauthUrl: string;
  qrCodeDataUrl: string | null;
}

interface ConfirmResponse {
  recoveryCodes: string[];
  accessToken: string;
  refreshToken: string;
}

/**
 * Two-factor enrolment.
 *
 * Reachable with an enrolment-scoped token, which is all an administrator holds
 * before they finish setup. Confirming returns a full session, so they land in
 * the admin portal without signing in twice.
 */
export default function TwoFactorSetup() {
  const navigate = useNavigate();
  const { refreshUser, signOut } = useAuth();

  const [setup, setSetup] = useState<SetupResponse | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api
      .post<SetupResponse>('/auth/2fa/setup')
      .then(setSetup)
      .catch((caught: Error) => setError(caught.message));
  }, []);

  async function confirm(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await api.post<ConfirmResponse>('/auth/2fa/confirm', { totpCode: code });
      tokens.set(result.accessToken, result.refreshToken);
      setRecoveryCodes(result.recoveryCodes);
      await refreshUser();
      toast.success('Two-factor authentication is on.');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That code is not valid.');
      setCode('');
    } finally {
      setLoading(false);
    }
  }

  // Recovery codes are shown exactly once — nothing can retrieve them
  // afterwards, so the user must acknowledge before moving on.
  if (recoveryCodes) {
    return (
      <Shell title="Save your recovery codes" step="Almost done">
        <Alert tone="warning" title="Shown only once">
          Store these somewhere safe. Each one can be used a single time to sign in if you lose
          access to your authenticator app.
        </Alert>

        <div className="my-6 grid grid-cols-2 gap-2 rounded-xl border border-border bg-muted p-4">
          {recoveryCodes.map((recoveryCode) => (
            <code key={recoveryCode} className="numeric text-center text-sm font-bold text-foreground">
              {recoveryCode}
            </code>
          ))}
        </div>

        <div className="space-y-3">
          <Button
            variant="outline"
            full
            onClick={() => {
              void navigator.clipboard.writeText(recoveryCodes.join('\n'));
              setSaved(true);
              toast.success('Copied to your clipboard.');
            }}
          >
            {saved ? <Check size={16} /> : <Copy size={16} />}
            {saved ? 'Copied' : 'Copy all codes'}
          </Button>
          <Button full size="lg" onClick={() => navigate('/admin', { replace: true })}>
            I have saved them — continue
          </Button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell title="Set up two-factor authentication" step="Required for administrators">
      <p className="mb-6 text-sm leading-relaxed text-foreground-secondary">
        This system moves members&rsquo; money, so every administrator account is protected by a
        second factor. It takes about a minute.
      </p>

      {error && (
        <Alert tone="danger" className="mb-5">
          {error}
        </Alert>
      )}

      {!setup ? (
        <div className="flex justify-center py-10">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : (
        <>
          <Step number={1} title="Open an authenticator app">
            <p className="text-sm text-foreground-secondary">
              Google Authenticator, Microsoft Authenticator, Authy or 1Password all work.
            </p>
          </Step>

          <Step number={2} title="Scan this code">
            {setup.qrCodeDataUrl ? (
              <div className="flex flex-col items-center gap-4 rounded-xl border border-border bg-white p-5">
                <img
                  src={setup.qrCodeDataUrl}
                  alt="QR code for two-factor authentication setup"
                  className="h-48 w-48"
                  width={192}
                  height={192}
                />
                <p className="text-center text-xs text-foreground-secondary">
                  Cannot scan? Enter the key below by hand instead.
                </p>
              </div>
            ) : (
              <p className="text-sm text-foreground-secondary">
                Enter the setup key below into your app.
              </p>
            )}

            <div className="mt-3 rounded-xl border border-border bg-muted p-4">
              <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-foreground-secondary">
                Setup key
              </p>
              <div className="flex items-center gap-2">
                <code className="numeric flex-1 break-all text-sm font-bold text-foreground">
                  {setup.secret}
                </code>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    void navigator.clipboard.writeText(setup.secret);
                    toast.success('Copied.');
                  }}
                  aria-label="Copy setup key"
                >
                  <Copy size={16} />
                </Button>
              </div>
            </div>
          </Step>

          <Step number={3} title="Enter the 6-digit code" last>
            <form onSubmit={confirm} className="space-y-4">
              <Field label="" htmlFor="code">
                <Input
                  id="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  autoFocus
                  className="numeric h-14 text-center text-2xl font-bold tracking-[0.4em]"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                />
              </Field>

              <Button type="submit" full size="lg" loading={loading} disabled={code.length !== 6}>
                <ShieldCheck size={16} /> Turn on two-factor authentication
              </Button>
            </form>
          </Step>
        </>
      )}

      <button
        type="button"
        onClick={() => {
          void signOut().then(() => navigate('/login', { replace: true }));
        }}
        className="mt-6 w-full text-center text-sm font-semibold text-foreground-secondary transition-colors hover:text-primary"
      >
        Sign out
      </button>
    </Shell>
  );
}

function Step({
  number,
  title,
  children,
  last,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
  last?: boolean;
}) {
  return (
    <div className="relative pb-6 pl-11 last:pb-0">
      {!last && <div className="absolute bottom-0 left-[15px] top-8 w-px bg-border" />}
      <span className="absolute left-0 top-0 flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
        {number}
      </span>
      <p className="mb-2 pt-1 font-heading font-bold text-foreground">{title}</p>
      {children}
    </div>
  );
}

function Shell({
  title,
  step,
  children,
}: {
  title: string;
  step: string;
  children: React.ReactNode;
}) {
  return (
    <AuthShell wide title={title}>
      <p className="mb-5 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-primary">
        <Smartphone size={12} /> {step}
      </p>
      <Card className="p-6 sm:p-8">
        <CardContent className="p-0">{children}</CardContent>
      </Card>
    </AuthShell>
  );
}
